/* eslint-disable react/jsx-props-no-spreading */
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import { Collapse } from "@mui/material"
import Alert from "@mui/material/Alert"
import AlertTitle from "@mui/material/AlertTitle"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { Dispatch, ReactElement, SetStateAction, useState } from "react"
import { TransitionGroup } from "react-transition-group"

import {
    INST_MAX_FILES_PER_DROP,
    INST_MAX_FILE_SIZE_BYTES,
    INST_MIN_FILE_SIZE_BYTES,
} from "../../../../constants/attachmentUploadRestrictions"
import { useScreenSize } from "../../../../hooks/useScreenSize"
import useUploadAttachments from "../../../../services/middleware/hooks/singleApplication/useUploadAttachment"
import { IUpload } from "../../../../services/middleware/types/singleApplication/uploadAttachmentTypes"
import { GRID_SPACING } from "../../../../themes/defaultThemeParams"
import AppDropzone from "../../../attachments/appDropzone"
import UploadedAttachment from "../../../attachments/uploadedAttachment"
import AppButton from "../../../buttons/appButton"
import CheckMarkList from "../../../dataDisplay/lsits/checkMarkList"
import ImageTips, { CX_TIPS, TIPS } from "../../../popUps/imageTips/imageTips"
import DisplayIf from "../../../wrappers/displayIf"

interface IAppDropzoneProps {
    uploads: IUpload[]
    imageMode: boolean
    setUploads: Dispatch<SetStateAction<IUpload[]>>
    uploadSideEffects?: (newStuff: IUpload[]) => void
    maxPerDrop?: number
    commissioning?: boolean
}

/**
 * Displays a dropzone for the user to upload their images to. Excepts
 * an array of IUploads and a setter for that array as inputs. `uploadSideEffects`
 * is an optional function that is called when the user uploads a file.
 * It is called inside the the `setUploads` setter function.
 *
 */
const SingleApplicationDropzone = ({
    uploads,
    setUploads,
    imageMode,
    uploadSideEffects,
    maxPerDrop = INST_MAX_FILES_PER_DROP,
    commissioning,
}: IAppDropzoneProps): ReactElement => {
    const { onDrop, deleteUpload, rejectedUploads, clearRejections } =
        useUploadAttachments(uploads, setUploads, maxPerDrop, uploadSideEffects)
    const { isTabletOrMob } = useScreenSize()
    const [showImageTips, setShowImageTips] = useState(false)

    return (
        <>
            <ImageTips
                isOpen={showImageTips}
                close={() => setShowImageTips(false)}
            />
            <Collapse in={rejectedUploads.length > 0}>
                <Alert
                    severity="error"
                    action={
                        <AppButton
                            onClick={clearRejections}
                            variant="text"
                            color="error"
                        >
                            Clear
                        </AppButton>
                    }
                >
                    <Stack direction="row" justifyContent="space-between">
                        <AlertTitle>
                            We were unable to upload some of your files
                        </AlertTitle>
                    </Stack>

                    <>
                        {Object.entries(rejectedUploads).map(([key, value]) => (
                            <Typography variant="body1" key={key}>
                                {value?.name} — <strong>{value?.reason}</strong>
                            </Typography>
                        ))}
                    </>
                </Alert>
            </Collapse>
            <Grid
                container
                spacing={isTabletOrMob ? 0 : GRID_SPACING}
                width="100%"
            >
                <Grid xs={12} lg={6}>
                    <AppDropzone
                        onDrop={onDrop}
                        maxFileSize={INST_MAX_FILE_SIZE_BYTES}
                        minFileSize={INST_MIN_FILE_SIZE_BYTES}
                        maxFilesPerDrop={maxPerDrop}
                    />
                </Grid>
                <DisplayIf condition={!isTabletOrMob}>
                    <Grid xs={12} lg={6}>
                        <Alert
                            severity="info"
                            sx={{
                                ml: 3,
                                borderRadius: 2,
                                px: 4,
                                py: 2,
                            }}
                            action={
                                <DisplayIf
                                    condition={imageMode && !commissioning}
                                >
                                    <AppButton
                                        sx={{ mt: 0 }}
                                        onClick={() => setShowImageTips(true)}
                                        variant="outlined"
                                        color="primary"
                                        endIcon={<LightbulbOutlinedIcon />}
                                    >
                                        Tips
                                    </AppButton>
                                </DisplayIf>
                            }
                        >
                            <AlertTitle sx={{ width: "100%" }}>
                                Please use this dropzone to upload your file(s)
                            </AlertTitle>
                            {imageMode ? (
                                <Typography variant="body1" component="span">
                                    {commissioning
                                        ? "Please provide the final copy of the circuit diagram and schedule of protection settings. Some tips:"
                                        : "A cut-out photo is required for all applications. Here are some tips for providing a good image"}

                                    <CheckMarkList
                                        items={commissioning ? CX_TIPS : TIPS}
                                    />
                                </Typography>
                            ) : (
                                <Typography variant="body1" component="span">
                                    Please upload any supplementing information
                                    relevant to your application. Some tips:
                                    <CheckMarkList
                                        items={[
                                            "This may include technical data sheets related to the devices selected, or any single line diagrams.",
                                            "A data-sheet is required for any new device which you have defined manually in the new devices section.",
                                            "Please name your files (prior to upload) in a way that makes it clear what they are!",
                                            "You can upload any number of files, but each must be less than 5MB in size.",
                                            "If you need to upload larger files, try zipping them and uploading the zip.",
                                            "No SVGs permitted. Please convert to PNG or JPEG",
                                        ]}
                                    />
                                </Typography>
                            )}
                        </Alert>
                    </Grid>
                </DisplayIf>

                <Grid xs={12} md={12} marginTop={3}>
                    <TransitionGroup
                        component={Stack}
                        spacing={GRID_SPACING * 1.5}
                    >
                        {uploads.map((upload) => (
                            <Collapse
                                key={upload.name}
                                timeout={600}
                                mountOnEnter
                                unmountOnExit
                            >
                                <UploadedAttachment
                                    upload={upload}
                                    deleteUpload={deleteUpload}
                                />
                            </Collapse>
                        ))}
                    </TransitionGroup>
                </Grid>
            </Grid>
        </>
    )
}

SingleApplicationDropzone.defaultProps = {
    uploadSideEffects: null,
    maxPerDrop: INST_MAX_FILES_PER_DROP,
    commissioning: false,
}

export default SingleApplicationDropzone
